<template>
  <section id="services" class="services-section">
    <div class="section-header">
      <div class="header-line"></div>
      <h2 class="section-title">Our <span>Services</span></h2>
      <div class="header-line"></div>
    </div>
    
    <p class="section-subtitle">Specialized Educational Solutions to Transform Your Institution</p>
    
    <div class="services-container">
      <div class="service-card" v-for="(service, index) in services" :key="index" :class="{ 'animate': isInView }" :style="{ 'animation-delay': `${0.15 * index}s` }">
        <div class="service-image">
          <img :src="service.image" :alt="service.title">
          <div class="service-overlay">
            <a href="#" class="read-more-btn" @click.prevent="openModal(service)">Read More</a>
          </div>
        </div>
        <div class="service-content">
          <h3>{{ service.title }}</h3>
          <p>{{ service.description }}</p>
        </div>
      </div>
    </div>
    
    <!-- Service Modal -->
    <div class="service-modal" v-if="modalOpen">
      <div class="modal-content">
        <button class="close-button" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
        
        <div class="modal-image">
          <img :src="currentService.image" :alt="currentService.title">
        </div>
        
        <div class="modal-details">
          <h2>{{ currentService.title }}</h2>
          <div class="modal-description">
            <p>{{ currentService.description }}</p>
            <p>{{ currentService.extendedDescription }}</p>
          </div>
          
          <div class="service-features">
            <h3>Key Benefits</h3>
            <ul>
              <li v-for="(feature, index) in currentService.features" :key="index">
                <i class="fas fa-check-circle"></i> {{ feature }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ServicesSection',
  data() {
    return {
      isInView: false,
      modalOpen: false,
      currentService: null,
      services: [
        {
          title: 'Training and Workshops',
          image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
          description: 'Comprehensive training programs and workshops designed to enhance faculty competencies, teaching methodologies, and assessment techniques for continuous professional development.',
          extendedDescription: 'Our tailored training and workshop solutions are meticulously designed to address the specific needs of your institution. We offer a wide range of topics from innovative teaching methodologies to assessment strategies, curriculum development, and leadership development. Each program is facilitated by experienced educators and industry professionals who bring real-world expertise to every session.',
          features: [
            'Customized workshops aligned with institutional goals',
            'Hands-on activities and practical applications',
            'Evidence-based instructional strategies',
            'Post-training support and implementation guidance',
            'Comprehensive resource materials for continued reference'
          ]
        },
        {
          title: 'Strategic Planning And Business Services',
          image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
          description: 'Expert guidance in developing comprehensive strategic plans aligned with your institution\'s vision, mission, and goals while maximizing operational efficiency and resource allocation.',
          extendedDescription: 'Our strategic planning services help educational institutions navigate complex challenges and identify opportunities for growth and improvement. We facilitate a collaborative process that involves stakeholders at all levels to create actionable, measurable plans. Our consultants bring decades of experience in higher education management and organizational development to help transform your institutional vision into reality.',
          features: [
            'Comprehensive organizational assessment and SWOT analysis',
            'Data-driven decision-making frameworks',
            'KPI development and performance measurement systems',
            'Resource optimization and financial sustainability planning',
            'Implementation roadmaps with clear timelines and responsibilities'
          ]
        },
        {
          title: 'Excellence And Quality Assurance',
          image: 'https://images.unsplash.com/photo-1558403194-611308249627?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
          description: 'Comprehensive quality assurance systems and frameworks tailored to your educational environment, ensuring continuous improvement and adherence to international standards.',
          extendedDescription: 'Our quality assurance services help institutions establish robust systems for maintaining and enhancing educational excellence. We work with you to develop comprehensive frameworks that address all aspects of quality, from academic programs to administrative processes. Our approach emphasizes continuous improvement and is aligned with international best practices and accreditation requirements.',
          features: [
            'Customized quality management systems',
            'Internal audit procedures and documentation',
            'Faculty and staff quality enhancement training',
            'Performance indicator development and tracking',
            'Continuous improvement mechanisms and feedback loops'
          ]
        },
        {
          title: 'Program Reviews And Accreditation Preparation',
          image: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
          description: 'Thorough program reviews and specialized accreditation preparation services to help your institution achieve recognition for educational excellence and meet accreditation standards.',
          extendedDescription: 'Navigating the complex landscape of program accreditation requires expertise and meticulous attention to detail. Our consultants have extensive experience with various accrediting bodies and can guide your institution through every step of the process. We conduct comprehensive program reviews to identify strengths and areas for improvement, then develop strategic action plans to address any gaps.',
          features: [
            'Gap analysis against accreditation standards',
            'Self-study report preparation and documentation',
            'Mock site visit preparation and training',
            'Remediation planning for identified deficiencies',
            'Post-accreditation quality maintenance strategies'
          ]
        },
        {
          title: 'Course And Program Development',
          image: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
          description: 'Innovative curriculum design and program development services that incorporate the latest educational trends, industry demands, and learning technologies.',
          extendedDescription: 'Our curriculum development specialists work closely with faculty and administration to create engaging, relevant, and rigorous academic programs. We blend pedagogical expertise with industry insights to ensure your offerings prepare students for real-world success. Our process includes market analysis, competency mapping, and alignment with institutional and accreditation requirements.',
          features: [
            'Market needs assessment and competitive analysis',
            'Outcome-based curriculum design',
            'Integration of emerging technologies and teaching methods',
            'Assessment strategy development',
            'Faculty development plans for new program implementation'
          ]
        },
        {
          title: 'Instructional Design',
          image: 'https://images.unsplash.com/photo-1470790376778-a9fbc86d70e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
          description: 'Expert instructional design services that employ research-based methodologies to create engaging learning experiences across various delivery modes including online, hybrid, and face-to-face.',
          extendedDescription: 'Our instructional design team combines educational theory with cutting-edge technology to create immersive, effective learning experiences. Whether you\'re developing online, blended, or traditional courses, we ensure your content is engaging, accessible, and pedagogically sound. We specialize in creating interactive elements, multimedia content, and authentic assessments that promote deep learning.',
          features: [
            'Learner-centered course design and development',
            'Multimedia content creation and integration',
            'Interactive activity development',
            'Accessibility compliance and universal design',
            'Learning management system optimization'
          ]
        }
      ]
    }
  },
  mounted() {
    window.addEventListener('scroll', this.checkView);
    // Initial check
    this.checkView();
    
    // Close modal on ESC key
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.checkView);
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    checkView() {
      const section = document.querySelector('.services-section');
      if (!section) return;
      
      const position = section.getBoundingClientRect();
      
      // Check if section is in viewport
      if (position.top < window.innerHeight && position.bottom >= 0) {
        this.isInView = true;
      }
    },
    openModal(service) {
      this.currentService = service;
      this.modalOpen = true;
      document.body.style.overflow = 'hidden'; // Prevent body scrolling
    },
    closeModal() {
      this.modalOpen = false;
      document.body.style.overflow = ''; // Restore body scrolling
    },
    handleKeyDown(e) {
      if (!this.modalOpen) return;
      
      if (e.key === 'Escape') {
        this.closeModal();
      }
    }
  }
}
</script>

<style scoped>
.services-section {
  padding: 8rem 2rem;
  background-color: white;
  position: relative;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.header-line {
  height: 2px;
  width: 80px;
  background: linear-gradient(90deg, transparent, #d1c06e, transparent);
  margin: 0 1.5rem;
}

.section-title {
  font-size: 2.5rem;
  color: #333;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
}

.section-title span {
  color: #d1c06e;
  position: relative;
}

.section-title span::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #d1c06e;
}

.section-subtitle {
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

.services-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.service-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.3s ease;
}

.service-card.animate {
  animation: slide-up 0.6s ease forwards;
}

@keyframes slide-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.service-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(209, 192, 110, 0.2);
}

.service-image {
  height: 220px;
  overflow: hidden;
  position: relative;
}

.service-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
}

.service-card:hover .service-image img {
  transform: scale(1.1);
}

.service-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(209, 192, 110, 0.9), rgba(209, 192, 110, 0.5));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.service-card:hover .service-overlay {
  opacity: 1;
}

.read-more-btn {
  background-color: white;
  color: #d1c06e;
  padding: 0.8rem 1.5rem;
  border-radius: 30px;
  font-weight: 600;
  text-decoration: none;
  transform: translateY(20px);
  opacity: 0;
  transition: all 0.4s ease;
  transition-delay: 0.1s;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.service-card:hover .read-more-btn {
  transform: translateY(0);
  opacity: 1;
}

.read-more-btn:hover {
  background-color: #d1c06e;
  color: white;
}

.service-content {
  padding: 1.5rem;
}

.service-content h3 {
  color: #333;
  font-size: 1.3rem;
  margin-bottom: 0.8rem;
  font-weight: 600;
}

/* Service Modal Styles */
.service-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-content {
  position: relative;
  background-color: white;
  border-radius: 12px;
  max-width: 85%;
  width: 1000px;
  max-height: 90vh;
  overflow: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.8);
  transform: scale(1.1);
}

.modal-image {
  width: 100%;
  height: 300px;
  overflow: hidden;
}

.modal-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.modal-details {
  padding: 2rem;
}

.modal-details h2 {
  color: #d1c06e;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
}

.modal-description {
  color: #555;
  line-height: 1.7;
  margin-bottom: 2rem;
}

.modal-description p {
  margin-bottom: 1rem;
}

.service-features h3 {
  color: #333;
  font-size: 1.4rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.service-features ul {
  list-style: none;
  padding: 0;
}

.service-features li {
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.8rem;
  color: #666;
}

.service-features li i {
  color: #d1c06e;
  margin-right: 10px;
  margin-top: 5px;
}

@media (max-width: 1100px) {
  .services-container {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .services-section {
    padding: 5rem 1.5rem;
  }
  
  .section-title {
    font-size: 2rem;
  }
  
  .services-container {
    grid-template-columns: 1fr;
  }
  
  .header-line {
    width: 50px;
  }
  
  .modal-content {
    max-width: 95%;
  }
  
  .modal-image {
    height: 200px;
  }
  
  .modal-details {
    padding: 1.5rem;
  }
  
  .modal-details h2 {
    font-size: 1.5rem;
  }
}
</style> 